import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, Clock, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface DuplicateMeetingProps {
  meetingId: string;
  meetingTitle: string;
  onDuplicate: (options: {
    title: string;
    date: Date;
    includeShared: boolean;
    agendaOption: 'all' | 'incomplete';
    includeNotes: boolean;
    actionsOption: 'none' | 'all' | 'move-incomplete';  // Updated this line
  }) => void;
}

export function DuplicateMeeting({ 
  meetingTitle, 
  onDuplicate, 
}: DuplicateMeetingProps) {
  const now = new Date();
  now.setMinutes(Math.floor(now.getMinutes() / 15) * 15);
  now.setSeconds(0);
  now.setMilliseconds(0);

  const [title, setTitle] = useState(`${meetingTitle}`); // Removed "Copy of" from the title
  const [date, setDate] = useState<Date>(now);
  const [time, setTime] = useState(format(now, 'HH:mm'));
  const [includeShared, setIncludeShared] = useState(false);
  const [agendaOption, setAgendaOption] = useState<'all' | 'incomplete'>('all');
  const [includeNotes, setIncludeNotes] = useState(false);
  const [actionsOption, setActionsOption] = useState<'none' | 'move-incomplete' | 'all'>('none');

  const timeOptions = Array.from({ length: 96 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  const handleDateTimeChange = (newDate: Date | undefined, newTime: string) => {
    if (newDate) {
      const [hours, minutes] = newTime.split(':').map(Number);
      const updatedDate = new Date(newDate);
      updatedDate.setHours(hours, minutes);
      setDate(updatedDate);
      setTime(newTime);
    }
  };

  const handleSubmit = async () => {
    const [hours, minutes] = time.split(':').map(Number);
    const finalDate = new Date(date);
    finalDate.setHours(hours, minutes);

    onDuplicate({
      title,
      date: finalDate,
      includeShared,
      agendaOption,
      includeNotes,
      actionsOption
    });
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button className="ml-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white border-transparent shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">
          <Copy className="h-4 w-4 mr-2" />
          Duplicate meeting
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Duplicate Meeting</AlertDialogTitle>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Meeting Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label>Date and Time</Label>
              <div className="flex items-center gap-4">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("justify-start text-left font-normal", !date && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={(newDate) => handleDateTimeChange(newDate, time)} initialFocus />
                  </PopoverContent>
                </Popover>

                <Select value={time} onValueChange={(newTime) => handleDateTimeChange(date, newTime)}>
                  <SelectTrigger className="w-[120px]">
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((timeOption) => (
                      <SelectItem key={timeOption} value={timeOption}>
                        {timeOption}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Shared with</Label>
              <Label className="flex items-center space-x-2">
                <Checkbox 
                  id="shared" 
                  checked={includeShared}
                  onCheckedChange={(checked) => setIncludeShared(checked as boolean)}
                />
                <span>All shared with emails</span>
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Agenda Items</Label>
              <RadioGroup value={agendaOption} onValueChange={(value) => setAgendaOption(value as 'all' | 'incomplete')}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="agenda-all" />
                  <Label htmlFor="agenda-all">All agenda items</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="incomplete" id="agenda-incomplete" />
                  <Label htmlFor="agenda-incomplete">Incomplete agenda items only</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Label className="flex items-center space-x-2">
                <Checkbox 
                  id="notes" 
                  checked={includeNotes}
                  onCheckedChange={(checked) => setIncludeNotes(checked as boolean)}
                />
                <span>Duplicate notes</span>
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Actions</Label>
              <RadioGroup value={actionsOption} onValueChange={(value) => setActionsOption(value as 'none' | 'move-incomplete' | 'all')}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="actions-none" />
                  <Label htmlFor="actions-none">None</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="move-incomplete" id="actions-move" />
                  <Label htmlFor="actions-move">Move incomplete actions</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="actions-all" />
                  <Label htmlFor="actions-all">All actions</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleSubmit} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">Duplicate</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
